import { Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { NavigationVisibility, RolePermission } from './types'
import { Role, roleColorMap, roleNameMap } from '../../domain/role.enum'
import { useQuery } from '@tanstack/react-query'
import { type Role as RoleDTO, UserApi } from '../../API/user'
import { getAllRolePermissions } from './utils'
import { PageContainer, PageHeader } from '../../components/page'

export const RolePermissionsPage = () => {
  const rolesQuery = useQuery<unknown, unknown, Array<RoleDTO>>({
    queryKey: ['roles'],

    queryFn: UserApi.getRoles
  })

  const columns: ColumnsType<RolePermission> = [
    {
      title: 'Page / Feature',
      dataIndex: 'page'
    },
    {
      title: 'Path',
      dataIndex: 'path',
      render: (path: string) => (
        <Typography.Link href={path} target='_blank'>
          {path}
        </Typography.Link>
      )
    },
    {
      title: 'Navigation Visibility',
      dataIndex: 'navigationVisibility',
      render: ({ value, color }: NavigationVisibility) => (
        <Tag key={value} color={color}>
          {value}
        </Tag>
      )
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      filters:
        rolesQuery.data?.map(role => ({
          text: role.name,
          value: role.id
        })) ?? [],
      onFilter: (value, record) => record.roles.includes(value as Role),
      render: (roles: Array<Role>) => (
        <Space wrap direction='horizontal' size={[2, 6]}>
          {roles.map(role => (
            <Tag key={role} color={roleColorMap[role]}>
              {roleNameMap[role]}
            </Tag>
          ))}
        </Space>
      )
    }
  ]

  const dataSource = getAllRolePermissions()

  const totalPages = dataSource.length
  const visiblePages = dataSource.filter(p => p.navigationVisibility.value === 'VISIBLE').length
  const notInMenuPages = dataSource.filter(
    p => p.navigationVisibility.value === 'NOT IN MENU'
  ).length

  return (
    <PageContainer className='space-y-4'>
      <div className='flex justify-between items-center'>
        <PageHeader />

        <div className='flex items-center gap-4 px-6 py-3 bg-white rounded-xl border border-gray-200 shadow-sm'>
          <div className='flex items-center gap-2'>
            <span className='text-xs text-gray-500 font-medium'>Total</span>
            <span className='text-lg font-semibold text-gray-800'>{totalPages}</span>
          </div>

          <div className='h-6 w-px bg-gray-200' />

          <div className='flex items-center gap-2'>
            <span className='text-xs text-gray-500 font-medium'>Visible</span>
            <span className='text-lg font-semibold text-green-600'>{visiblePages}</span>
          </div>

          <div className='h-6 w-px bg-gray-200' />

          <div className='flex items-center gap-2'>
            <span className='text-xs text-gray-500 font-medium'>Hidden</span>
            <span className='text-lg font-semibold text-red-600'>{notInMenuPages}</span>
          </div>
        </div>
      </div>

      <Table columns={columns} dataSource={dataSource} pagination={false} bordered />
    </PageContainer>
  )
}
